import { Popover, Button, makeStyles, PopoverActions } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import React, { useCallback, useRef } from 'react'
import useGetCompensation from '../../../../hooks/query/useGetCompensation'
import Content from './Content'
import { Separator } from './Separator'
import ErrorContent from './ErrorContent'
import LoadingContent from './LoadingContent'
import { useTranslation } from 'react-i18next'
import DisplayFormatedNumber from '../../../../components/DisplayFormatedNumber'
import { PayoutPopupContext } from './context'
import { CompensationResponse } from '../../../../api/salaryCalculation'

export type CompensationPopupProps = {
  onClose: () => void
  anchorEl: HTMLTableCellElement
  date: string
  practitionerReferenceId: string
  isInitialCompensation: boolean
}

const isTotalBonusBiggerThanGuaranteed = (data: CompensationResponse) =>
  data.totalBonusPayout >= (data.guaranteedPayout ?? 0)

export const CompensationPopup = ({
  onClose,
  anchorEl,
  date,
  practitionerReferenceId,
  isInitialCompensation,
}: CompensationPopupProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const popoverAction = useRef<PopoverActions>(null)
  const { isLoading, data, error } = useGetCompensation({
    practitionerReferenceId,
    date,
    type: 'new',
    initialCompensation: isInitialCompensation,
  })

  const updatePopoverPosition = useCallback(() => {
    setTimeout(() => {
      popoverAction.current?.updatePosition()
    }, 0)
  }, [])

  return (
    <Popover
      open
      anchorEl={anchorEl}
      classes={{ paper: classes.popover }}
      action={popoverAction}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      onClose={onClose}
      disableScrollLock
    >
      <div className={classes.container}>
        <div className={classes.header}>
          <div>
            {t('compensation_popup_header')} {date}
          </div>
          <Button onClick={onClose}>
            <CloseIcon />
          </Button>
        </div>

        <Separator />

        <div className={classes.content}>
          {isLoading ? <LoadingContent /> : null}
          {error ? <ErrorContent /> : null}
          {data ? (
            <PayoutPopupContext.Provider
              value={{
                data,
                updatePopoverPosition,
                isTotalBonusBiggerThanGuaranteed: isTotalBonusBiggerThanGuaranteed(data),
              }}
            >
              <Content />
            </PayoutPopupContext.Provider>
          ) : null}
        </div>

        <Separator />

        {data ? (
          <div className={classes.summary}>
            <div className={classes.totalLabel}>{t('compensation_popup_total_label')}</div>
            <div className={classes.totalValue}>
              <DisplayFormatedNumber value={data.total} format="euroInt" />
            </div>
          </div>
        ) : null}
      </div>
    </Popover>
  )
}

const useStyles = makeStyles({
  popover: {
    borderRadius: '16px',
  },
  container: {
    padding: '24px 16px',
    width: 535,
    minHeight: 260,
    boxShadow: '0px 1px 2px rgba(0, 20, 15, 0.05), 0px 0px 16px rgba(0, 20, 15, 0.1)',
    borderRadius: 16,
    boxSizing: 'border-box',
  },
  header: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '19px',
    lineHeight: '22px',
    letterSpacing: '0.5px',
    color: '#212121',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    '& > button': {
      minWidth: 'unset',
      padding: 0,
    },
    marginLeft: 8,
    marginBottom: 16,
  },
  content: {
    padding: '8px 0px',
  },
  summary: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginLeft: 8,
    marginTop: 24,
  },
  totalValue: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '19px',
    lineHeight: '22px',
    letterSpacing: '0.5px',
    color: '#00624F',
    marginRight: 24,
  },
  totalLabel: {
    fontFamily: 'Lato',
    fontWeight: 700,
    fontSize: '19px',
    lineHeight: '22px',
    letterSpacing: '0.5px',
    color: '#212121',
  },
})
